import { forwardRef, ForwardRefExoticComponent, PropsWithoutRef, RefAttributes } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useFormContext as useReactHookFormContext } from 'react-hook-form';
import { useFormFieldStyles } from './use-form-field-styles';

import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';

import * as SDK from '@siloreal:shared:sdk';

type FR<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

type Props = {
  tid: string;
  name: string;
  required?: boolean;
  size?: 'medium' | 'large';
};

enum AREA_CODE {
  AR = '54',
  BR = '55',
}

export const phone: FR<HTMLDivElement, Props> = forwardRef((props, ref) => {
  const { name, size } = props;
  const { required = false } = props;

  const { control } = useFormContext();
  const {
    setError,
    clearErrors,
    formState: { errors },
  } = useReactHookFormContext();
  const hasError = Boolean(errors[name]);
  const errorMessage = errors[name]?.message as string | undefined;

  const extract = {
    area: (phone: string) => phone.slice(0, 2).trim() as AREA_CODE,
    number: (phone: string) => phone.slice(2).trim(),
  };

  const AREA = {
    label: 'Área',
    [AREA_CODE.AR]: {
      flag: '🇦🇷',
      label: 'Argentina',
      prefix: '+54',
      value: AREA_CODE.AR,
    },
    [AREA_CODE.BR]: {
      flag: '🇧🇷',
      label: 'Brasil',
      prefix: '+55',
      value: AREA_CODE.BR,
    },
  };
  const PHONE = {
    label: 'Teléfono',
    placeholder: '1234567890',
  };

  const fieldSx = useFormFieldStyles({ size, slots: 2 });

  return (
    <FormControl sx={fieldSx} size="small" error={hasError}>
      <Controller
        name={name}
        control={control}
        defaultValue={AREA_CODE.AR}
        rules={{
          validate: (value) => {
            const currentPhoneNumber = extract.number(value);
            const result = SDK.validator.form.phone(currentPhoneNumber, { required });
            return SDK.core.match.result(result, {
              ok: () => true,
              err: (msg) => msg,
            });
          },
        }}
        render={({ field }) => (
          <Stack ref={ref} direction="row" gap={2} alignItems="center">
            <FormControl size="small" style={{ width: '64px', maxWidth: '64px', minWidth: '64px' }}>
              <InputLabel>{AREA.label}</InputLabel>
              <Select
                label={AREA.label}
                renderValue={() => <>{AREA[extract.area(field.value)].flag}</>}
                value={extract.area(field.value)}
                error={hasError}
                sx={{
                  backgroundColor: '#FFF',
                }}
                onChange={(e) => {
                  const newAreaCode = e.target.value;
                  const currentPhoneNumber = extract.number(field.value);
                  field.onChange(`${newAreaCode}${currentPhoneNumber}`);
                }}
              >
                {[AREA[AREA_CODE.AR], AREA[AREA_CODE.BR]].map((area) => (
                  <MenuItem key={area.label} value={area.value}>
                    {area.flag} {area.label} <span style={{ color: '#a7a7a7' }}>&nbsp;({area.prefix})</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="text"
              size="small"
              label={PHONE.label}
              required={required}
              error={hasError}
              sx={{ flex: 1 }}
              placeholder={PHONE.placeholder}
              value={extract.number(field.value)}
              onChange={async (e) => {
                const newPhoneDigits = e.target.value;
                const currentAreaCode = extract.area(field.value);

                // Trigger validation
                const fullPhone = `${currentAreaCode}${newPhoneDigits}`;
                const result = SDK.validator.form.phone.change(fullPhone);
                const isValid = SDK.core.match.result(result, {
                  ok: () => true,
                  err: (message) => {
                    setError(name, { type: 'manual', message });
                    return false;
                  },
                });
                if (isValid) clearErrors(name);
                else return;

                field.onChange(`${currentAreaCode}${newPhoneDigits}`);
              }}
              onBlur={() => {
                SDK.core.match.result(SDK.validator.form.phone.blur(field.value), {
                  ok: () => clearErrors(name),
                  err: (message) => setError(name, { type: 'manual', message }),
                });
              }}
              slotProps={{
                input: {
                  style: { backgroundColor: '#fff' },
                  startAdornment: (
                    <Box
                      sx={{
                        color: '#A7A7A7',
                      }}
                    >
                      {AREA[extract.area(field.value)].prefix}&nbsp;
                    </Box>
                  ),
                  endAdornment: <PhoneIphoneIcon sx={{ color: '#A7A7A7' }} />,
                },
              }}
            />
          </Stack>
        )}
      />
      <FormHelperText>{errorMessage}</FormHelperText>
    </FormControl>
  );
});

phone.displayName = 'SiloReal-Form-Field-Phone';
